import { GroupAgentPreviewBanner } from '@ms/yammer-groups';
import { useExperimentTreatmentWithoutReporting } from '@ms/yammer-hooks/dist/experiments';
import { useIsPrivateContentModeActiveForViewer } from '@ms/yammer-hooks/dist/featureAccessSettings/selectors';
import { useSettings } from '@ms/yammer-hooks/dist/pushNotifications';
import { useIsCurrentUserRestricted } from '@ms/yammer-hooks/dist/session/selectors';
import { useViewerIsPreviewing } from '@ms/yammer-network-brand-theme/dist/store';
import { PushNotificationsDeprecationBanner } from '@ms/yammer-push-notifications-deprecation-banner';
import PrivateContentModeBanner from '@ms/yammer-web-components/dist/admin/PrivateContentModeBanner';
import { useLeadershipCornerBanner } from '@ms/yammer-web-components/dist/leadershipCorner/LeadershipCornerBanner';
import { useOutageBanner } from '@ms/yammer-web-components/dist/outageBanner/hooks';
import RestrictedUserBanner from '@ms/yammer-web-components/dist/user/RestrictedUserBanner';
import Flex, { FlexItem } from '@ms/yammer-web-core-ui/dist/components/Flex';
import React, { Suspense, lazy, useCallback } from 'react';

import { useHasCommunityAgentPreviewBanner } from './useHasCommunityAgentPreviewBanner';

// Lazy load the BrandCenterPreviewBanner to reduce main bundle size
const BrandCenterPreviewBanner = lazy(() =>
  import(
    /* webpackChunkName: "3-render-admin-brand-center" */ '@ms/yammer-admin-center/dist/BrandCenterPreviewBanner'
  ).then((module) => ({
    default: module.BrandCenterPreviewBanner,
  }))
);

export const useGetBannersCallback = () => {
  const { enabled: arePushNotificationsEnabled } = useSettings();
  const isDeprecationBannerEnabled =
    useExperimentTreatmentWithoutReporting('PWANotificationDeprecationBanner') === true;
  const outageBanner = useOutageBanner();
  const leadershipCornerBanner = useLeadershipCornerBanner();
  const shouldShowRestrictedUserBanner = useIsCurrentUserRestricted();
  const isPrivateContentModeActive = useIsPrivateContentModeActiveForViewer();
  const isCommunityAgentPreviewEnabled = useHasCommunityAgentPreviewBanner();
  const viewerIsPreviewing = useViewerIsPreviewing();
  const isCustomThemingEnabled = useExperimentTreatmentWithoutReporting('EngageCustomBrandingTheming') === true;
  const shouldShowPWANotificationDeprecationBanner = isDeprecationBannerEnabled && arePushNotificationsEnabled;
  const brandCenterPreviewBanner = isCustomThemingEnabled && viewerIsPreviewing;

  return useCallback(() => {
    const hasAnyBanner =
      outageBanner ||
      leadershipCornerBanner ||
      shouldShowRestrictedUserBanner ||
      isPrivateContentModeActive ||
      isCommunityAgentPreviewEnabled ||
      brandCenterPreviewBanner ||
      shouldShowPWANotificationDeprecationBanner;
    const outageBannerContent = outageBanner ? <FlexItem>{outageBanner}</FlexItem> : null;
    const leadershipCornerBannerContent = leadershipCornerBanner ? <FlexItem>{leadershipCornerBanner}</FlexItem> : null;
    const PWANotificationDeprecationBannerContent = shouldShowPWANotificationDeprecationBanner ? (
      <FlexItem>
        <PushNotificationsDeprecationBanner />
      </FlexItem>
    ) : null;
    const communityAgentPreviewBanner = isCommunityAgentPreviewEnabled ? <GroupAgentPreviewBanner /> : null;
    const restrictedUserBannerContent = shouldShowRestrictedUserBanner ? (
      <FlexItem>
        <RestrictedUserBanner />
      </FlexItem>
    ) : null;
    const privateContentModeBannerContent = isPrivateContentModeActive ? (
      <FlexItem>
        <PrivateContentModeBanner />
      </FlexItem>
    ) : null;
    const previewCustomThemingAdminPreviewContent = brandCenterPreviewBanner ? (
      <Suspense fallback={null}>
        <FlexItem>
          <BrandCenterPreviewBanner />
        </FlexItem>
      </Suspense>
    ) : null;

    const renderContent = hasAnyBanner ? (
      <Flex grow={1} direction="column">
        {privateContentModeBannerContent}
        {outageBannerContent}
        {leadershipCornerBannerContent}
        {restrictedUserBannerContent}
        {previewCustomThemingAdminPreviewContent}
        {communityAgentPreviewBanner}
        {PWANotificationDeprecationBannerContent}
      </Flex>
    ) : null;

    return renderContent;
  }, [
    outageBanner,
    leadershipCornerBanner,
    isCommunityAgentPreviewEnabled,
    shouldShowRestrictedUserBanner,
    isPrivateContentModeActive,
    brandCenterPreviewBanner,
    shouldShowPWANotificationDeprecationBanner,
  ]);
};
